import { Controller } from "stimulus";

export default class extends Controller {
  static targets = ["form"];

  connect() {
    // Pre-bind handlers so removeEventListener works correctly
    this._onSubmit        = this.handleSubmit.bind(this);
    this._onAjaxSuccess   = this.handleAjaxSuccess.bind(this);
    this._onFrameLoad     = this.handleTurboUpdate.bind(this);
    this._onBeforeStream  = this.handleTurboStream.bind(this);
    this._onThemeChanged  = this.onThemeChanged.bind(this);

    if (this.hasFormTarget) {
      this.formTarget.addEventListener("submit", this._onSubmit);
      this.formTarget.addEventListener("ajax:success", this._onAjaxSuccess);
    }

    document.addEventListener("turbo:frame-load", this._onFrameLoad);
    document.addEventListener("turbo:before-stream-render", this._onBeforeStream);
    document.addEventListener("theme:changed", this._onThemeChanged);
  }

  disconnect() {
    if (this.hasFormTarget) {
      this.formTarget.removeEventListener("submit", this._onSubmit);
      this.formTarget.removeEventListener("ajax:success", this._onAjaxSuccess);
    }
    document.removeEventListener("turbo:frame-load", this._onFrameLoad);
    document.removeEventListener("turbo:before-stream-render", this._onBeforeStream);
    document.removeEventListener("theme:changed", this._onThemeChanged);
  }

  // -------- FORM FLOW --------
  handleSubmit() {
    const select = this.getThemeSelect();
    this.pendingTheme = select ? select.value : null;
  }

  handleAjaxSuccess(event) {
    const [data, _status, xhr] = event.detail;
    let theme = null;

    if (xhr.getResponseHeader("Content-Type")?.includes("application/json")) {
      try {
        const json = typeof data === "string" ? JSON.parse(data) : data;
        theme = json?.theme ?? null;
      } catch (e) { console.error("Theme JSON parse error:", e); }
    }

    if (!theme) theme = this.pendingTheme || this.getCurrentThemeValue();
    if (theme) {
      this.reconcileEditorTheme(theme);
      this.applyTheme(theme);
    }
  }

  handleTurboUpdate()   { this.syncFromDom(); }
  handleTurboStream()   { setTimeout(() => this.syncFromDom(), 100); }

  syncFromDom() {
    const current = this.getCurrentThemeValue();
    if (current) this.applyTheme(current);
  }

  getCurrentThemeValue() {
    const select = this.getThemeSelect();
    return select ? select.value : null;
  }

  getThemeSelect() {
    const selectors = [
      'select[name="ai_options[theme]"]',
      'select[name*="theme"]',
      '#ai_options_theme'
    ];
    for (const s of selectors) {
      const el = document.querySelector(s);
      if (el) return el;
    }
    return null;
  }

  // -------- TOGGLE BUTTON --------
  toggleTheme() {
    const aiOptionsId = this.getAiOptionsId();
    if (!aiOptionsId) {
      console.error("AiOptions ID not found");
      return;
    }

    fetch(`/ai_options/${aiOptionsId}/toggle_theme`, {
      method: "POST",
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
        "Content-Type": "application/json"
      }
    })
      .then(r => r.json())
      .then(({ theme }) => {
        if (!theme) return;
        this.reconcileEditorTheme(theme);
        this.applyTheme(theme);
        // ensure the form select matches server state
        const select = this.getThemeSelect();
        if (select) select.value = theme;
      })
      .catch(err => console.error("Error toggling theme:", err));
  }

  getAiOptionsId() {
    // Prefer this instance’s element, else any element carrying the id
    return (
      this.element?.dataset?.aiOptionsId ||
      document.querySelector("[data-ai-options-id]")?.dataset?.aiOptionsId ||
      null
    );
  }

  reconcileEditorTheme(theme) {
    const delivery = document.getElementById('publishForm')?._documentDelivery;
    if (!delivery?.settings.has('theme')) return;
    // A legacy Settings save supersedes any failed or in-flight quick choice.
    // Serialize through the same session so a later retry cannot restore it.
    delivery.session.save(delivery.setSetting('theme', theme));
  }

  // -------- APPLY + BROADCAST --------
  applyTheme(theme) {
    // Update global theme holders
    [document.getElementById("currentTheme"), document.getElementById("currentSidebarTheme")]
      .forEach(el => {
        if (!el) return;
        el.className = theme === "dark" ? "chat-theme-dark" : "chat-theme-light";
      });

    this.pendingTheme = null;

    // Broadcast so all controller instances (even in other frames) sync their UI
    document.dispatchEvent(new CustomEvent("theme:changed", { detail: { theme } }));
  }

  // Any instance that hears a theme change syncs *its own* controls (checkbox + select)
  onThemeChanged(e) {
    const theme = e.detail?.theme;
    if (!theme) return;

    // Sync all toggle checkboxes globally (works across frames)
    document.querySelectorAll('[data-theme-toggle="switch"]').forEach(cb => {
      cb.checked = (theme === "dark");
    });

    // Keep all theme selects in sync (if multiple are present in DOM/frames)
    document.querySelectorAll('select[name="ai_options[theme]"]').forEach(sel => {
      sel.value = theme;
    });
  }
}
